import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Library, Search, Play, Eye, User, FileQuestion, GraduationCap } from "lucide-react";

export default function BibliotecaQuizzes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState(null);
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    api
      .get("/quizzes/public/library")
      .then((r) => setQuizzes(r.data))
      .catch(() => setQuizzes([]));
  }, []);

  const subjects = useMemo(
    () => [...new Set((quizzes || []).map((q) => q.subject).filter(Boolean))].sort(),
    [quizzes]
  );
  const grades = useMemo(
    () => [...new Set((quizzes || []).map((q) => q.grade).filter(Boolean))].sort(),
    [quizzes]
  );

  const filtered = useMemo(() => {
    let list = quizzes || [];
    const s = search.trim().toLowerCase();
    if (s)
      list = list.filter(
        (q) =>
          (q.title || "").toLowerCase().includes(s) ||
          (q.creator_name || "").toLowerCase().includes(s)
      );
    if (subject) list = list.filter((q) => q.subject === subject);
    if (grade) list = list.filter((q) => q.grade === grade);
    return list;
  }, [quizzes, search, subject, grade]);

  const apply = async (quizId, count) => {
    if (!user) {
      toast.info("Entre na sua conta para aplicar o quiz à sua turma.");
      navigate("/login");
      return;
    }
    if (!count) {
      toast.error("Este quiz não possui questões.");
      return;
    }
    setStarting(quizId);
    try {
      const res = await api.post("/games/start", { quiz_id: quizId });
      navigate(`/apresentar/${res.data.session_id}`, { state: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao aplicar o quiz.");
    } finally {
      setStarting(null);
    }
  };

  const selectCls =
    "h-11 rounded-full border border-slate-200 bg-white px-4 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-blue/40";

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <MarketingNav />
      <main className="flex-1 max-w-6xl mx-auto w-full px-5 sm:px-8 py-12">
        <div className="flex items-center gap-3 text-brand-purple mb-3">
          <span className="w-11 h-11 rounded-2xl bg-brand-purple/10 flex items-center justify-center">
            <Library className="w-6 h-6" />
          </span>
          <span className="text-sm font-semibold uppercase tracking-widest">Comunidade EDUCARTE</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black text-brand-ink">Biblioteca de Quizzes</h1>
        <p className="mt-3 text-brand-muted max-w-2xl">
          Explore quizzes criados por professores de todo o Brasil. Aplique com a sua turma ao vivo ou copie para a sua
          conta e personalize do seu jeito.
        </p>

        {/* Filtros */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-brand-muted absolute left-4 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título ou professor..."
              className="h-11 pl-10 rounded-full border-slate-200"
              data-testid="library-search"
            />
          </div>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={selectCls}
            data-testid="library-subject-filter"
          >
            <option value="">Todas as disciplinas</option>
            {subjects.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className={selectCls}
            data-testid="library-grade-filter"
          >
            <option value="">Todas as séries</option>
            {grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        {quizzes === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center">
            <div className="w-20 h-20 rounded-3xl bg-white shadow-sm border border-slate-100 flex items-center justify-center mx-auto">
              <FileQuestion className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="mt-6 font-heading font-bold text-xl text-brand-ink">Nenhum quiz encontrado</h3>
            <p className="mt-2 text-brand-muted">
              {quizzes.length === 0
                ? "Ainda não há quizzes publicados na biblioteca."
                : "Tente ajustar a busca ou os filtros."}
            </p>
          </div>
        ) : (
          <>
            <p className="mt-8 text-sm text-brand-muted" data-testid="library-count">
              {filtered.length} quiz{filtered.length === 1 ? "" : "zes"} disponíve{filtered.length === 1 ? "l" : "is"}
            </p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="library-grid">
              {filtered.map((q) => (
                <div
                  key={q.quiz_id}
                  data-testid={`library-quiz-${q.quiz_id}`}
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow flex flex-col"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-blue/10 text-brand-blue">
                      {q.subject || "Geral"}
                    </span>
                    <span className="text-xs text-brand-muted flex items-center gap-1">
                      <FileQuestion className="w-3.5 h-3.5" /> {q.question_count}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-lg text-brand-ink line-clamp-2 min-h-[3.5rem]">
                    {q.title}
                  </h3>
                  {q.grade && (
                    <p className="text-sm text-brand-muted flex items-center gap-1.5">
                      <GraduationCap className="w-4 h-4" /> {q.grade}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-brand-muted flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> por {q.creator_name}
                  </p>
                  <div className="mt-auto pt-4 flex gap-2">
                    <Button
                      onClick={() => navigate(`/biblioteca/${q.quiz_id}`)}
                      variant="outline"
                      className="rounded-full gap-1.5 flex-1"
                      data-testid={`preview-${q.quiz_id}`}
                    >
                      <Eye className="w-4 h-4" /> Ver
                    </Button>
                    <Button
                      onClick={() => apply(q.quiz_id, q.question_count)}
                      disabled={starting === q.quiz_id}
                      className="rounded-full gap-1.5 flex-1 bg-brand-green hover:bg-emerald-600"
                      data-testid={`apply-${q.quiz_id}`}
                    >
                      {starting === q.quiz_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}{" "}
                      Aplicar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
